import type { BannerType } from '@sunwebgroup/ui-components';
import {ServiceType} from "../../domain/types";

import * as React from 'react';

import { ListItem, RadioGroup, SpaceBetween, tk, Typography, Banner } from '@sunwebgroup/ui-components';

import { useIntl } from '@/intl';
import {useService} from '@/scopes/checkout/domain/hooks';

import ServiceBox from './ServiceBox';
import {updatePerBookingService} from "../../domain/actions";
import { useSendAncillaryAvailable } from '@/scopes/checkout/domain/analytics/useSendAncillaryAvailable';
import { createAncillariesClickedEvent } from '../../domain/analytics';
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks';
import { Price, Texts } from '@/scopes/checkout/components/services/ServiceComponents';

export function ParkingAirportService() {
  const intl = useIntl();
  const { service, setServiceValue } = useService(ServiceType.ParkingAirport);
  const { sendEvent } = useAnalytics();

  const handleValueChange = React.useCallback(
    (value: string) => {
      if (!service) return;
      setServiceValue(updatePerBookingService({ value })(service));

      const optionClicked = service.options.find(( option) => option.value === value);
      sendEvent(
        createAncillariesClickedEvent({
          result: optionClicked?.price || 0,
          type: optionClicked?.label || '',
          description: 'selectParkingAirport',
          element: 'radiobutton',
        })
      );
    },
    [sendEvent, setServiceValue, service]
  );

  useSendAncillaryAvailable(service, 'selectParkingAirport');

  if (!service) return null

  const { options, banners, selectedValue } = service

  return (
    <ServiceBox
      title={intl.formatMessage({ id: 'checkout_steps_ancillaries_parking_airport_title' })}
      description={intl.formatMessage({ id: 'checkout_steps_ancillaries_parking_airport_description' })}
      glyph="IconParking"
      modalTitle={intl.formatMessage({ id: 'checkout_steps_ancillaries_parking_airport_modal_title' })}
      modalDescription={intl.formatMessage({ id: 'checkout_steps_ancillaries_parking_airport_modal_description' })}
    >
      <SpaceBetween direction="y" spacing={tk('spacing_sm')}>
        <RadioGroup.Root value={selectedValue ?? null} onValueChange={handleValueChange}>
          <SpaceBetween direction="y" spacing={tk('spacing_xs')}>
            {options.map(({ label, badge, price, value }) => (
              <ListItem.Root key={value}>
                <ListItem.Control>
                  <RadioGroup.Item value={value} />
                </ListItem.Control>
                <ListItem.Content>
                  {badge && <ListItem.Badge $state="success">{badge}</ListItem.Badge>}
                  <ListItem.Label>{label}</ListItem.Label>
                </ListItem.Content>
                <ListItem.Trailing>
                  <Price as="span" $variant="label">
                    {intl.formatCurrency(price)}
                  </Price>
                </ListItem.Trailing>
              </ListItem.Root>
            ))}
          </SpaceBetween>
        </RadioGroup.Root>
        {banners.map(({ title, text, type = 'info' }, index) => (
          <Banner key={`banner-${index}`} $type={type as BannerType}>
            <Texts direction="y">
              {title && <Typography $variant="label">{title}</Typography>}
              {text && <Typography dangerouslySetInnerHTML={{ __html: text }} />}
            </Texts>
          </Banner>
        ))}
      </SpaceBetween>
    </ServiceBox>
  );
}
